/**
 * Shared helpers for the Teams-style meeting screens.
 *
 * Realtime data-message topics must match /^[A-Za-z0-9_-]{1,36}$/ and payloads must stay
 * under 2048 bytes. Chime never delivers a message back to its sender, so every send is
 * also applied locally by the caller.
 */
export const TOPIC_CHAT = "du-chat";
export const TOPIC_HAND = "du-hand";
export const TOPIC_REACT = "du-react";
export const TOPIC_CTRL = "du-ctrl";

/** Chat is retained for five minutes so people who join late still see recent messages. */
export const CHAT_LIFETIME_MS = 300000;
export const CHAT_MAX_CHARS = 500;
const DATA_MESSAGE_MAX_BYTES = 2000;

export type ReactionType = "like" | "heart" | "clap" | "laugh" | "wow";

export const REACTIONS: { type: ReactionType; emoji: string; label: string }[] = [
  { type: "like", emoji: "👍", label: "Like" },
  { type: "heart", emoji: "❤️", label: "Love" },
  { type: "clap", emoji: "👏", label: "Applause" },
  { type: "laugh", emoji: "😆", label: "Laugh" },
  { type: "wow", emoji: "😮", label: "Surprised" },
];

export function reactionEmoji(type: string): string {
  return REACTIONS.find((reaction) => reaction.type === type)?.emoji || "";
}

export interface ChatMessage {
  id: string;
  senderId: string;
  sender: string;
  local: boolean;
  text: string;
  at: number;
}

export interface TranscriptLine {
  id: string;
  speakerId: string;
  speaker: string;
  /** The words as transcribed, kept so the line can be redacted again after the speaker label arrives. */
  raw: string;
  /** `raw` with spoken PHI hidden (`redactPhi`): what is shown and saved. */
  text: string;
  at: number;
}

export interface MeetingPerson {
  attendeeId: string;
  externalUserId: string;
  local: boolean;
  tileId: number | null;
  paused: boolean;
  muted: boolean | null;
  speaking: boolean;
  signal: number | null;
  handRaisedAt: number | null;
  reaction: string | null;
}

export interface MeetingLeftEvent {
  reason: "left" | "ended" | "other-device" | "removed" | "error";
  durationMs: number;
  /** The host chose "End meeting for all" rather than leaving on their own. */
  endedForAll?: boolean;
}

export function initialsOf(name: string): string {
  const parts = (name || "").replace(/@.*/, "").split(/[\s._-]+/).filter(Boolean);
  if (!parts.length) return "?";
  return parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join("");
}

/** Teams-style avatar colours: a soft background with a darker foreground, picked by a stable hash. */
const AVATAR_COLORS: [string, string][] = [
  ["#e8ebfa", "#4f52b2"],
  ["#fde7e9", "#a4262c"],
  ["#dff6dd", "#0b6a0b"],
  ["#fff4ce", "#835b00"],
  ["#deecf9", "#0f548c"],
  ["#f4e0f7", "#7b1a8c"],
  ["#d9f4f2", "#00666d"],
  ["#fbe5d6", "#8e3b10"],
];

export function avatarColor(seed: string): { background: string; color: string } {
  let hash = 0;
  for (let i = 0; i < (seed || "").length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  const [background, color] = AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
  return { background, color };
}

export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (value: number) => String(value).padStart(2, "0");
  return hours ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
}

export function formatClock(at: number): string {
  return new Date(at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export function fitsDataMessage(payload: any): boolean {
  return new TextEncoder().encode(JSON.stringify(payload)).length <= DATA_MESSAGE_MAX_BYTES;
}

export function newMessageId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

export function meetingLink(code: string): string {
  return new URL("meeting/join/" + encodeURIComponent(code), document.baseURI).href;
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const area = document.createElement("textarea");
    area.value = text;
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    return ok;
  }
}
